/* eslint-disable react-hooks/exhaustive-deps */
import './index.scss'

import React, { useRef, useState, forwardRef, useImperativeHandle } from 'react'
import T from 'prop-types'
import BEMHelper from 'react-bem-helper'
import Icon from 'components/Icon'

const bem = new BEMHelper('accordion')

const AccordionComponent = (
  {
    title,
    titleComponent: TitleComponent = ({ children, className }) => (
      <h2 className={className}>{children}</h2>
    ),
    description,
    values,
    children,
    spaceless,
    full,
    small,
    large,
    section,
    rounded,
    subtle,
    subtleValues,
    onToggle,
    defaultExpanded,
    showAllValues = false,
    customMaxHeight,
    ...props
  },
  ref,
) => {
  const wrapperRef = useRef()
  const [height, setHeight] = useState(null)
  const [expanded, setExpanded] = useState(defaultExpanded || false)
  const [doneAnimating, setDoneAnimating] = useState(true)

  const list = !(props.list === false || small || rounded || subtle || section)

  const toggle = (value) => () => {
    const newState = value || (value === false && value) || !expanded
    setExpanded(newState)
    setDoneAnimating(false)

    if (onToggle) {
      onToggle(newState)
    }

    if (newState) {
      wrapperRef.current.focus()
      setHeight(`${wrapperRef.current.scrollHeight}px`)
    }
  }

  useImperativeHandle(ref, () => ({
    toggle(value) {
      toggle(value)()
    },
  }))

  const handleTransitionEnd = () => {
    setDoneAnimating(true)
  }

  return (
    <div
      {...bem('', {
        expanded,
        spaceless,
        section,
        full,
        list,
        rounded,
        subtle,
        small,
        large,
      })}
    >
      <button type="button" {...bem('toggle')} onClick={toggle()} aria-expanded={expanded}>
        <TitleComponent {...bem('title')}>{title}</TitleComponent>

        {description && <p {...bem('description')}>{description}</p>}

        <Icon icon="chevron" size="small" direction="down" {...bem('indicator')} />

        {values && (
          <ul {...bem('values', { 'show-all': showAllValues })}>
            {values.map((item, index) => (
              <li key={index} {...bem('value', { subtle: subtleValues })}>
                {item}
              </li>
            ))}
          </ul>
        )}
      </button>
      <div
        {...bem('wrapper', { expanded: doneAnimating && expanded })}
        ref={wrapperRef}
        style={
          expanded
            ? { maxHeight: doneAnimating ? customMaxHeight ?? '1000px' : height }
            : { maxHeight: '0px' }
        }
        onTransitionEnd={handleTransitionEnd}
        tabIndex="-1"
      >
        <div {...bem('content', { hidden: doneAnimating && !expanded })}>{children}</div>
      </div>
    </div>
  )
}

AccordionComponent.displayName = 'Accordion'

AccordionComponent.propTypes = {
  title: T.string.isRequired,
  children: T.any.isRequired,
  onToggle: T.func,
  spaceless: T.bool,
  list: T.bool,
  small: T.bool,
  large: T.bool,
  full: T.bool,
  section: T.bool,
  rounded: T.bool,
  subtle: T.bool,
  defaultExpanded: T.bool,
  subtleValues: T.bool,
  values: T.array,
  showAllValues: T.bool,
  titleComponent: T.func,
  description: T.string,
  customMaxHeight: T.string,
}

const Accordion = forwardRef(AccordionComponent)

export default Accordion
